import { Trending, BinaryLobbyFeed, CategoryBanner, SubCategoryBar, LobbyBannerSlot } from '@/components/features';
import { useAppSearchParams } from '@/hooks/useAppSearchParams';
import { useSignupTrack, useTrackImpression } from '@/analytics';

/**
 * Main application page — the predictions feed.
 *
 * The top bar (logo, greeting, balance, category tabs) is owned by the
 * app shell's singleton `HomeTopBar`; this page only renders the feed:
 * the curated Trending lineup when no category is selected, otherwise the
 * binary-question lobby feed (ARC-82 §4). Search is opened from the search
 * icon in the category tab bar (`CategoryTabs`), not a field in the feed.
 */
export function HomePage() {
  const { isTrending } = useAppSearchParams();
  const track = useSignupTrack();
  // `landing_page` — first platform view, top of the signup funnel. Once per
  // session (the impression hook dedups by key), even though this catch-all
  // route can remount on navigation.
  //
  // threshold: 0 (fire as soon as any pixel is visible) is deliberate: this ref
  // wraps the full-height lobby, which grows past ~2× the viewport once the
  // async feed renders. With the default 0.5 threshold the impression only fires
  // if the observer's initial callback happens to land before the feed grows —
  // a race that fails intermittently (and consistently in CI). The event means
  // "the lobby was shown", so any-pixel-visible is the correct trigger.
  const landingRef = useTrackImpression('landing_page', () => track('landing_page', {}), { threshold: 0 });

  return (
    <div ref={landingRef} className="flex flex-col min-h-svh pb-[calc(82px+env(safe-area-inset-bottom))] md:pb-0">
      <div className="flex flex-col items-center gap-4 md:gap-5 lg:gap-6 md:px-6 lg:px-8 xl:px-10 mt-2">
        {/* Swipeable banner strip above the categories (ARC-112). Picks the
            lander (guest) vs lobby (authed) surface by auth state and self-nulls
            when there are no banners. */}
        <LobbyBannerSlot />
        {/* Self-nulls on Trending and for categories without a banner. */}
        <CategoryBanner />
        {/* Sub-category chips directly on top of the list (not sticky —
            scrolls away with the banner). Self-nulls like the banner. */}
        <SubCategoryBar />
        {isTrending ? <Trending /> : <BinaryLobbyFeed />}
      </div>
    </div>
  );
}
